import {
  Error as ErrorIcon,
  Info as InfoIcon,
  WarningAmber,
  Edit as EditIcon,
  History as HistoryIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from "@mui/icons-material";
import { Chip, Stack, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Vehicle, useArchiveVehicleMutation, useRestoreVehicleMutation } from "../../../api/vehiclesApi";
import {
  Column,
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { ConfirmationDialog } from "../../../components/common/ConfirmationDialog/ConfirmationDialog";
import { formatDateTime } from "../../../utils/dateUtils";
import { getVehicleStatusColor } from "../../../utils/statusColors";
import AuditHistoryDialog from "../../audit/components/AuditHistoryDialog";

interface VehiclesTableProps {
  vehicles: Vehicle[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  
  // Search functionality
  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const VehiclesTable: React.FC<VehiclesTableProps> = ({
  vehicles,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,
  
  // Search functionality
  enableSearch = false,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [historyVehicle, setHistoryVehicle] = useState<Vehicle | null>(null);
  const [archiveVehicle, { isLoading: isArchiving }] = useArchiveVehicleMutation();
  const [restoreVehicle] = useRestoreVehicleMutation();
  const [vehicleToArchive, setVehicleToArchive] = useState<Vehicle | null>(null);

  const handleEditClick = (vehicleId: number) => {
    navigate(`/vehicles/${vehicleId}/edit`);
  };

  const handleArchiveConfirm = async () => {
    if (vehicleToArchive) {
      await archiveVehicle(vehicleToArchive.id);
      setVehicleToArchive(null);
    }
  };

  const handleRestoreClick = async (vehicle: Vehicle) => {
    await restoreVehicle(vehicle.id);
  };

  const columns: Column[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "make",
      headerName: "Make",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "year",
      headerName: "Year",
      flex: 1,
      renderCell: (value: number | null) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value !== null ? value : "-"}
        </Typography>
      ),
    },
    {
      field: "vin",
      headerName: "VIN",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "serial",
      headerName: "Serial",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "dataSource",
      headerName: "Data Source",
      flex: 1,
      renderCell: (value: string) => (
        <Typography
          sx={{
            fontSize: "0.8125rem",
            color: "text.primary",
          }}
        >
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "lastStatus",
      headerName: "Status",
      flex: 1,
      renderCell: (value: string) => (
        <span
          style={{
            color: getVehicleStatusColor(value),
            fontWeight: "bold",
          }}
        >
          {value || "Unknown"}
        </span>
      ),
    },
    {
      field: "faultStatus",
      headerName: "Fault Status",
      width: 200,
      renderCell: (_: any, vehicle: Vehicle) => {
        const hasActiveLights =
          vehicle.lastEmissionsLightOn ||
          vehicle.lastProtectLightOn ||
          vehicle.lastStopLightOn ||
          vehicle.lastWarningLightOn ||
          vehicle.lastCheckEngineLightOn;

        if (!hasActiveLights) return null;

        const activeLights = [];
        if (vehicle.lastEmissionsLightOn) activeLights.push("Emissions");
        if (vehicle.lastProtectLightOn) activeLights.push("Protect");
        if (vehicle.lastStopLightOn) activeLights.push("Stop");
        if (vehicle.lastWarningLightOn) activeLights.push("Warning");
        if (vehicle.lastCheckEngineLightOn) activeLights.push("Check Engine");

        // Custom colors and icons for different severity levels
        const getChipStyle = () => {
          if (vehicle.lastStopLightOn || vehicle.lastProtectLightOn) {
            return {
              icon: <ErrorIcon />,
              style: {
                backgroundColor: "error.light",
                color: "error.dark",
                borderColor: "error.main",
                border: 1,
                "& .MuiSvgIcon-root": {
                  color: "error.dark",
                },
              },
            };
          }
          if (vehicle.lastWarningLightOn || vehicle.lastEmissionsLightOn) {
            return {
              icon: <WarningAmber />,
              style: {
                backgroundColor: "warning.light",
                color: "warning.dark",
                borderColor: "warning.main",
                border: 1,
                "& .MuiSvgIcon-root": {
                  color: "warning.dark",
                },
              },
            };
          }
          return {
            icon: <InfoIcon />,
            style: {
              backgroundColor: "info.light",
              color: "info.dark",
              borderColor: "info.main",
              border: 1,
              "& .MuiSvgIcon-root": {
                color: "info.dark",
              },
            },
          };
        };

        const chipConfig = getChipStyle();

        return (
          <Tooltip
            title={
              <Stack
                spacing={0.5}
                sx={{ bgcolor: "background.paper", p: 1, borderRadius: 1 }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="text.primary"
                >
                  Active Warning Lights:
                </Typography>
                <Typography variant="caption" color="text.primary">
                  {activeLights.join(", ")}
                </Typography>

                {(vehicle.lastFaultSpnDescription ||
                  vehicle.lastFaultFmiDescription) && (
                  <>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ mt: 1 }}
                      color="text.primary"
                    >
                      Active Fault:
                    </Typography>
                    {vehicle.lastFaultSpnDescription && (
                      <Typography variant="caption" color="text.primary">
                        System: {vehicle.lastFaultSpnDescription}
                      </Typography>
                    )}
                    {vehicle.lastFaultFmiDescription && (
                      <Typography variant="caption" color="text.primary">
                        Status: {vehicle.lastFaultFmiDescription}
                      </Typography>
                    )}
                  </>
                )}

                {(vehicle.lastDtcDescription || vehicle.lastDtcCode) && (
                  <>
                    <Typography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ mt: 1 }}
                      color="text.primary"
                    >
                      DTC Information:
                    </Typography>
                    {vehicle.lastDtcDescription && (
                      <Typography variant="caption" color="text.primary">
                        {vehicle.lastDtcDescription}
                      </Typography>
                    )}
                    {vehicle.lastDtcCode && (
                      <Typography variant="caption" color="text.primary">
                        Code: {vehicle.lastDtcCode}
                      </Typography>
                    )}
                  </>
                )}

                {vehicle.lastFaultCanBusType && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 1 }}
                    color="text.primary"
                  >
                    CAN Bus Type: {vehicle.lastFaultCanBusType}
                  </Typography>
                )}
              </Stack>
            }
          >
            <Chip
              icon={chipConfig.icon}
              label={activeLights.join(", ")}
              size="small"
              sx={{
                height: "24px",
                ...chipConfig.style,
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  px: 1,
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "lastFaultTime",
      headerName: "Last Fault",
      width: 150,
      renderCell: (value: string | null) => formatDateTime(value),
    },
    {
      field: "isArchived",
      headerName: "Status",
      width: 120,
      renderCell: (value: boolean) =>
        value ? (
          <Chip label="Archived" size="small" color="default" variant="outlined" />
        ) : (
          <Chip label="Active" size="small" color="success" variant="outlined" />
        ),
    },
  ];

  // Define actions for each vehicle
  const getActions = (vehicle: Vehicle) => [
    {
      icon: <EditIcon fontSize="small" />,
      label: "Edit",
      onClick: () => handleEditClick(vehicle.id),
      color: "primary" as const,
    },
    {
      icon: <HistoryIcon fontSize="small" />,
      label: "View History",
      onClick: () => setHistoryVehicle(vehicle),
      color: "info" as const,
    },
    vehicle.isArchived
      ? {
          icon: <UnarchiveIcon fontSize="small" />,
          label: "Restore",
          onClick: () => handleRestoreClick(vehicle),
          color: "success" as const,
        }
      : {
          icon: <ArchiveIcon fontSize="small" />,
          label: "Archive",
          onClick: () => setVehicleToArchive(vehicle),
          color: "warning" as const,
        },
  ];

  // Define searchable columns for vehicles
  const searchableColumns = makeColumnsSearchable(
    columns,
    ['name', 'make', 'model', 'year', 'vin', 'serial', 'dataSource', 'lastStatus'],
    {
      name: 'string',
      make: 'string', 
      model: 'string',
      year: 'number',
      vin: 'string',
      serial: 'string',
      dataSource: 'string',
      lastStatus: 'string'
    }
  );

  return (
    <>
      <DataTable<Vehicle>
        items={vehicles}
        columns={columns}
        actions={getActions}
        isLoading={isLoading}
        title="Vehicles"
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        isLoadingRows={isLoadingRows}
        onLoadMore={onLoadMore}
        
        // Search functionality
        enableSearch={enableSearch}
        searchableColumns={searchableColumns}
        onSearchChange={onSearchChange}
      />
      {historyVehicle && (
        <AuditHistoryDialog
          open={Boolean(historyVehicle)}
          entityType="Vehicle"
          entityId={historyVehicle.id}
          title={`History for ${historyVehicle.name || `Vehicle #${historyVehicle.id}`}`}
          onClose={() => setHistoryVehicle(null)}
        />
      )}
      <ConfirmationDialog
        open={Boolean(vehicleToArchive)}
        title="Archive Vehicle"
        message={`Are you sure you want to archive "${vehicleToArchive?.name}"? It will no longer appear on the map or in the sidebar.`}
        confirmLabel="Archive"
        cancelLabel="Cancel"
        confirmColor="warning"
        onClose={() => setVehicleToArchive(null)}
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving}
      />
    </>
  );
};

export default VehiclesTable;
